import { useEffect, useState } from 'react';
import { Plus, Trash2, Loader2, Copy, Check, AlertTriangle, X } from 'lucide-react';
import { useSession } from '../../lib/auth.js';

const API_BASE = import.meta.env.VITE_API_BASE ?? '';

export default function Webhooks() {
  const { workspaces } = useSession();
  const ws = workspaces && workspaces[0];
  const [watchlists, setWatchlists] = useState([]);
  const [destinations, setDestinations] = useState({});
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(null);
  const [secretModal, setSecretModal] = useState(null);

  const load = async () => {
    if (!ws) return;
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE}/v1/watchlists?workspace_id=${ws.id}`, { credentials: 'include' });
      if (r.ok) {
        const data = await r.json();
        const wls = data.watchlists || [];
        setWatchlists(wls);
        const destMap = {};
        for (const wl of wls) {
          const dr = await fetch(`${API_BASE}/v1/watchlists/${wl.id}/destinations`, { credentials: 'include' });
          if (dr.ok) {
            const dd = await dr.json();
            destMap[wl.id] = dd.destinations || [];
          }
        }
        setDestinations(destMap);
      }
    } catch (_) { /* swallow */ }
    setLoading(false);
  };

  useEffect(() => { load(); }, [ws?.id]);

  const handleDelete = async (wlId, destId) => {
    if (!confirm('Delete this webhook destination?')) return;
    await fetch(`${API_BASE}/v1/watchlists/${wlId}/destinations/${destId}`, { method: 'DELETE', credentials: 'include' });
    load();
  };

  const handleCreate = async (wlId, url) => {
    const r = await fetch(`${API_BASE}/v1/watchlists/${wlId}/destinations`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ kind: 'webhook', config: { url } }),
    });
    if (r.ok) {
      const data = await r.json();
      setShowCreate(null);
      if (data.signing_secret) {
        setSecretModal(data.signing_secret);
      }
      load();
    }
  };

  return (
    <div>
      <h1 style={title}>Webhooks</h1>

      {loading ? (
        <div style={center}><Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} /></div>
      ) : watchlists.length === 0 ? (
        <p style={muted}>Create a watchlist first, then add webhook destinations.</p>
      ) : (
        watchlists.map((wl) => (
          <div key={wl.id} style={section}>
            <div style={sectionHeader}>
              <span style={sectionTitle}>{wl.name}</span>
              <button onClick={() => setShowCreate(wl.id)} style={addBtn}><Plus size={12} /> Add Webhook</button>
            </div>
            {(destinations[wl.id] || []).length === 0 ? (
              <p style={muted}>No destinations</p>
            ) : (
              (destinations[wl.id] || []).map((d) => {
                const cfg = typeof d.config === 'string' ? JSON.parse(d.config) : d.config;
                return (
                  <div key={d.id} style={destRow}>
                    <div>
                      <span style={destUrl}>{cfg?.url || ' - '}</span>
                      <span style={destMeta}>{d.kind}  -  {d.enabled ? 'active' : 'disabled'}</span>
                    </div>
                    <button onClick={() => handleDelete(wl.id, d.id)} style={delBtn}><Trash2 size={14} /></button>
                  </div>
                );
              })
            )}

            {showCreate === wl.id && (
              <CreateWebhookInline
                onSubmit={(url) => handleCreate(wl.id, url)}
                onCancel={() => setShowCreate(null)}
              />
            )}
          </div>
        ))
      )}

      {secretModal && (
        <SecretModal secret={secretModal} onClose={() => setSecretModal(null)} />
      )}
    </div>
  );
}

function CreateWebhookInline({ onSubmit, onCancel }) {
  const [url, setUrl] = useState('');
  return (
    <div style={inlineForm}>
      <input
        type="url"
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        placeholder="https://example.com/webhook"
        required
        style={urlInput}
      />
      <button onClick={() => url && onSubmit(url)} style={addBtn}>Create</button>
      <button onClick={onCancel} style={cancelBtn}>Cancel</button>
    </div>
  );
}

function SecretModal({ secret, onClose }) {
  const [copied, setCopied] = useState(false);
  const masked = '••••••••••••••••••••••••' + secret.slice(-4);

  const copy = async () => {
    await navigator.clipboard.writeText(secret);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div style={overlay}>
      <div style={modal}>
        <div style={modalHeader}>
          <h2 style={{ margin: 0, fontSize: 18, color: 'var(--fg-0)' }}>Signing Secret</h2>
          <button onClick={onClose} style={closeBtn}><X size={18} /></button>
        </div>
        <div style={warnRow}>
          <AlertTriangle size={16} />
          <span>Copy this secret now. It will not be shown again.</span>
        </div>
        <div style={keyRow}>
          <code style={keyText}>{masked}</code>
          <button onClick={copy} style={copyBtnStyle}>
            {copied ? <Check size={14} /> : <Copy size={14} />}
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
        <button onClick={onClose} style={doneBtn}>I have saved my secret</button>
      </div>
    </div>
  );
}

const title = { fontSize: 'var(--fs-22)', fontWeight: 600, color: 'var(--fg-0)', marginBottom: 'var(--space-5)' };
const center = { textAlign: 'center', padding: 'var(--space-8)', color: 'var(--fg-2)' };
const muted = { color: 'var(--fg-3)', fontSize: 'var(--fs-13)' };
const section = { marginBottom: 'var(--space-5)' };
const sectionHeader = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-2)' };
const sectionTitle = { fontSize: 'var(--fs-14)', fontWeight: 500, color: 'var(--fg-0)' };
const addBtn = { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 12px', background: 'var(--accent)', color: 'var(--accent-fg)', border: 'none', borderRadius: 'var(--r-1)', fontSize: 'var(--fs-12)', cursor: 'pointer' };
const cancelBtn = { padding: '4px 12px', background: 'var(--bg-3)', color: 'var(--fg-2)', border: '1px solid var(--line-1)', borderRadius: 'var(--r-1)', fontSize: 'var(--fs-12)', cursor: 'pointer' };
const destRow = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 'var(--space-2) var(--space-3)', background: 'var(--bg-2)', borderRadius: 'var(--r-1)', border: '1px solid var(--line-1)', marginBottom: 4 };
const destUrl = { fontSize: 'var(--fs-13)', color: 'var(--fg-0)', fontFamily: 'var(--font-mono)', display: 'block' };
const destMeta = { fontSize: 'var(--fs-11)', color: 'var(--fg-3)' };
const delBtn = { background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', padding: 4 };
const inlineForm = { display: 'flex', gap: 'var(--space-1)', marginTop: 'var(--space-2)' };
const urlInput = { flex: 1, padding: '4px 8px', background: 'var(--bg-3)', border: '1px solid var(--line-1)', borderRadius: 'var(--r-1)', color: 'var(--fg-0)', fontSize: 'var(--fs-13)' };
const overlay = { position: 'fixed', inset: 0, background: 'rgba(10,10,10,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 };
const modal = { background: 'var(--bg-1)', borderRadius: 12, padding: 28, maxWidth: 520, width: '100%', border: '1px solid var(--line-2)' };
const modalHeader = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 };
const closeBtn = { background: 'transparent', border: 0, cursor: 'pointer', color: 'var(--fg-2)', padding: 4 };
const warnRow = { display: 'flex', alignItems: 'center', gap: 8, padding: 12, background: 'var(--accent-soft)', borderRadius: 6, color: 'var(--accent)', fontSize: 13, marginBottom: 16 };
const keyRow = { display: 'flex', gap: 8, border: '1px solid var(--line-2)', borderRadius: 6, overflow: 'hidden', marginBottom: 16 };
const keyText = { flex: 1, padding: '12px 14px', fontFamily: 'var(--font-mono)', fontSize: 13, background: 'var(--bg-2)', color: 'var(--fg-0)', wordBreak: 'break-all' };
const copyBtnStyle = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0 16px', background: 'var(--accent)', color: 'var(--accent-fg)', border: 0, cursor: 'pointer', fontSize: 13, fontWeight: 600 };
const doneBtn = { width: '100%', padding: '12px 16px', background: 'var(--bg-2)', border: '1px solid var(--line-2)', borderRadius: 6, cursor: 'pointer', fontSize: 14, fontWeight: 600, color: 'var(--fg-1)' };
